import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { GridComponent } from '@iv/shared/components/grid/grid.component';
import { OptionsCalculatorComponent } from 'app/calculators/options-calculator/options-calculator.component';
import { ProbabilityCalculatorComponent } from 'app/calculators/probability-calculator/probability-calculator.component';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { ColorThemeService } from 'app/common/services/color-theme.service';
import { ThemeColor } from 'app/common/enums/theme-color.enum';
import { getCurrentTab } from '@iv/store/tabs/tabs.selectors';
import { getPermissions } from '@iv/store/permissions/permissions.reducer';
import { PermissionTypes } from '@iv/store/permissions/permissions.models';
import { getTime } from '@iv/shared/helpers/common.helper';
import { TabTypeEnum } from '@iv/model/tabs.model';
import { FuturesCalculatorComponent } from 'app/calculators/futures-calculator/futures-calculator.component';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { ContractViewerDialogComponent } from '../dialogs/contract-viewer-dialog/contract-viewer-dialog.component';
import { DateType } from 'app/charts-dashboard/interfaces';
import { IContractViewerDialogData } from '../../interfaces/option-monitor.interface';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'iv-live-option-monitor-table',
  templateUrl: './option-monitor-table.component.html',
  styleUrls: ['./option-monitor-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class OptionMonitorTableComponent extends GridComponent implements OnInit, AfterViewInit {

  constructor(
    protected _eref: ElementRef,
    @Inject(AppStore) protected store: Redux.Store<AppState>,
    private readonly colorThemeService: ColorThemeService,
    @Inject(TuiDialogService) private readonly dialogService: TuiDialogService,
    @Inject(Injector) private readonly injector: Injector,
  ) {
    super(_eref, store);
  }

  @Input() instrument;
  @Input() serviceResultElementRef: HTMLDivElement;
  @ViewChild(OptionsCalculatorComponent, {static: false}) optionsCalculator !: OptionsCalculatorComponent;
  @ViewChild(FuturesCalculatorComponent, {static: false}) futuresCalculator !: FuturesCalculatorComponent;
  @ViewChild(ProbabilityCalculatorComponent, {static: false}) probabilityCalculator !: ProbabilityCalculatorComponent;
  @Output() expirationButtonClicked = new EventEmitter<string>();
  @Output() strikeButtonClicked = new EventEmitter<string>();
  @Output() showCalculatorMessage = new EventEmitter<any>();
  @Output() setGridHeaderTitles = new EventEmitter<string[]>();

  public isDarkMode: boolean;
  public tabTypeEnum = TabTypeEnum;
  public currentTab = getCurrentTab(this.store.getState());
  public isFutures = this.currentTab.type.includes('future');
  public calculatorType: 'options' | 'probability' = 'options';
  mode: PermissionTypes = getPermissions(this.store.getState()).find(permission => {
    if (this.currentTab.type === TabTypeEnum.OPTION_CHAIN) {
      return permission === PermissionTypes.DELAYED_STOCKS || permission === PermissionTypes.RT_STOCKS;
    } else {
      return permission === PermissionTypes.EOD_FUTURES || permission === PermissionTypes.RT_FUTURES;
    }
  });
  isDelayed = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.mode === PermissionTypes.DELAYED_STOCKS : this.mode === PermissionTypes.EOD_FUTURES;
  isRt = this.currentTab.type === TabTypeEnum.OPTION_CHAIN ? this.mode === PermissionTypes.RT_STOCKS : this.mode === PermissionTypes.RT_FUTURES;
  selectedInstrument: any = {};
  calculatorsPopupPopupVisible = false;
  public tabType = getCurrentTab(this.store.getState()).type;

  private isFirstLaunch = true;
  isEODOptions = false;
  isEODProbability = false;
  regionIdOptions;
  regionIdProbability;

  protected readonly sessionStorage: Storage = sessionStorage;

  public ngOnInit(): void {
    super.ngOnInit();

    this.mode.toLowerCase();
    this.isDarkMode = this.colorThemeService.getColorTheme() === ThemeColor.DARK;
  }

  public ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.calcAllColsWidth(this.serviceResultElementRef);
  }

  onExpirationButtonClick(expiration: string) {
    this.expirationButtonClicked.emit(expiration);
  }

  onStrikeButtonClick(strike: string) {
    this.strikeButtonClicked.emit(strike);
  }

  trackByFn(index, item) {
    return `${item.stockid}:${item.expirationdate || item.expiration || item.optexpdate}:${item.strike}`;
  }

  getTrackValue(item) {
    return `${item.stockid}:${item.expirationdate || item.expiration || item.optexpdate}:${item.strike}`;
  }

  onCalculatorButtonClick(item: any): void {
    this.selectedInstrument = {
      [this.tabType === TabTypeEnum.OPTION_CHAIN ? 'stockId' : 'underlyingId']: item.call_optionsymbol.replace(/\s+/g, ''),
      stockName: item.call_optionsymbol,
      id: this.tabType !== TabTypeEnum.OPTION_CHAIN ? this.instrument.underlyingId : undefined,
      value: 'calc'
    };
    this.calculatorsPopupPopupVisible = true;
  }

  closeCalculatorWindow() {
    this.onPopupCancel('calculatorsPopupPopupVisible');
    this.optionsCalculator?.clearAll();
    this.probabilityCalculator.clearAll();
    this.futuresCalculator?.clearAll();
    this.optionsCalculator?.ngOnDestroy();
    this.probabilityCalculator.ngOnDestroy();
    this.futuresCalculator?.ngOnDestroy();
    this.selectedInstrument = null;
  }

  onPopupCancel(popupVisible) {
    this[popupVisible] = false;
  }

  switchCalculatorMode(): void {
    this.switchModeOptions();
    this.switchModeProbability();
  }

  private switchModeOptions(): void {
    this.optionsCalculator?.switchMode();
    this.isEODOptions = !this.isEODOptions;
  }

  private switchModeProbability(): void {
    this.probabilityCalculator?.switchMode();
    this.isEODProbability = !this.isEODProbability;
  }

  returnRegionOptions(region: number): void {
    this.regionIdOptions = region;
    if (this.regionIdOptions === 2 || this.regionIdOptions === 3 || this.regionIdOptions === 4) {
      this.isEODOptions = true;
    }
  }

  returnRegionProbability(region: number): void {
    this.regionIdProbability = region;
    if (this.regionIdProbability === 2 || this.regionIdProbability === 3 || this.regionIdProbability === 4) {
      this.isEODProbability = true;
    }
  }

  showMessage(event) {
    this.showCalculatorMessage.emit({message: event.message, type: event.type});
  }

  getQuoteTime(row: any) {
    return row ? row.substring(0, 10).replace(/-/g, '/').concat(' ', getTime(row)) : '';
  }

  protected getTableHeader(visibleColumns) {
    const header = [];
    if (visibleColumns.length > 0) {

      for (const column in this.columns) {
        if (visibleColumns.indexOf(column) > -1) {
          if ((column === 'put_optionsymbol' || column === 'call_optionsymbol') && this.isFirstLaunch) {
            this.columns[column] = {
              ...this.columns[column],
              minWidth: this.columns[column].minWidth + 20,
              width: this.columns[column].width + 20
            };
          }
          header.push(this.columns[column]);
        }
      }

      this.isFirstLaunch = false;
    }

    this.setHeaderTitles(header)

    return header;
  }

  public openContractViewer(row, optionSymbol): void {
    const data: IContractViewerDialogData = {
      optionSymbolCall: row['call_optionsymbol'],
      optionSymbolPut: row['put_optionsymbol'],
      instrument: this.instrument,
      expDate: row.expirationdate as DateType,
      strike: row.strike
    };
    this.dialogService.open(new PolymorpheusComponent(ContractViewerDialogComponent, this.injector), {data, size: 'l'})
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public switchCalculatorType(type: 'options' | 'probability'): void {
    this.calculatorType = type;
  }

  /** Emits array of header titles to parent (needed to download csv fields in the correct order) */
  private setHeaderTitles(header):void {
    const headerTitles = []
    header.forEach(item => {
      headerTitles.push(item.dataField)
    })
    this.setGridHeaderTitles.emit(headerTitles)
  }
}
